import React, { useCallback, useEffect, useState } from "react";
import EditorCloseIcon from "@atlaskit/icon/glyph/editor/close";
import { useQuery } from "react-query";
import Spinner from "@atlaskit/spinner";
import Checkbox from "@atlaskit/checkbox";
import {
  FieldContainer,
  Label,
  LabelIconContainer,
  SmallIconContainer,
  ItalicInfo,
  List,
  OptionLabel,
  OptionWrapper,
  SelectableUserWrapper,
  SpinnerContainer,
  WideButton,
} from "./styled";
import { StyledLabel } from "./StyledHelpers";
import { PickerOptionTypes } from "./option-types";
import { useTheme } from "styled-components";
import { fetchRecords } from "../../../../../../../api/cosmos-api";
import { fetchDetailsForUsers } from "../../../../../../../api/people-api";
import { useStructureStore } from "../store/structure-store";

function UsersFromCollection({
  selectedUser,
  collectionData,
  onSelect,
  onCancel,
}) {
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersInCollection, setUsersInCollection] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const theme = useTheme();

  const { checkIsUserInStructure } = useStructureStore((state) => ({
    checkIsUserInStructure: state.checkIsUserInStructure,
  }));

  const buildUserObject = useCallback(
    (user) => ({
      renderLabel: () => (
        <StyledLabel type={PickerOptionTypes.USER} data={user} />
      ),
      value: user.accountId,
      type: PickerOptionTypes.USER,
      name: user.displayName,
      isInStructure: checkIsUserInStructure(user.accountId),
    }),
    [checkIsUserInStructure]
  );

  const fetchUserDetails = async (accountIds) => {
    setLoadingUsers(true);
    try {
      const response = await fetchDetailsForUsers(accountIds);
      const users = JSON.parse(response.body);

      if (users?.results?.length) {
        setUsersInCollection(
          users.results.map(({ user }) => buildUserObject(user))
        );
      } else {
        setUsersInCollection([]);
      }
    } catch (error) {
      setUsersInCollection([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const { isLoading: loadingRecords, data: recordsData } = useQuery(
    [
      "userdatabase-records",
      {
        filters: [
          ...collectionData?.filters,
          {
            isMultiUser: true,
            type: "BOOLEAN",
            value: true,
            column: "hidden",
            condition: "NOT",
          },
        ],
        confluenceGroup: collectionData?.userGroup || "",
      },
    ],
    fetchRecords,
    {
      enabled: !!collectionData,
      retry: 0,
      select: (response) => {
        const { data } = response;
        return { users: data?.hits || [], totalSize: data?.totalSize || 0 };
      },
    }
  );

  useEffect(() => {
    if (!loadingRecords && recordsData) {
      if (recordsData?.users?.length) {
        fetchUserDetails(recordsData?.users?.map((user) => user.accountId));
      } else {
        setUsersInCollection([]);
      }
    }
  }, [loadingRecords, recordsData]);

  const handleChangeInCheckbox = (isChecked, userId) => {
    if (isChecked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(
        selectedUsers.filter((userAccountId) => userAccountId !== userId)
      );
    }
  };

  const isLoading = loadingRecords || loadingUsers;

  return (
    <FieldContainer>
      <LabelIconContainer>
        <Label>
          Select users from {collectionData?.name}
          {selectedUser?.name ? ` below ${selectedUser.name}` : ""}
        </Label>
        <SmallIconContainer
          onClick={() => onCancel()}
          style={{ marginBottom: "5px" }}
        >
          <EditorCloseIcon primaryColor="#6b778c" />
        </SmallIconContainer>
      </LabelIconContainer>
      {isLoading && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {!isLoading && (
        <>
          {usersInCollection?.length ? (
            <>
              <List>
                {usersInCollection.map(
                  ({ renderLabel, value, isInStructure }) => (
                    <SelectableUserWrapper key={value}>
                      {renderLabel()}
                      <OptionWrapper
                        style={{ gridTemplateColumns: "auto auto" }}
                      >
                        <ItalicInfo>
                          {isInStructure && "Already added"}
                        </ItalicInfo>
                        <Checkbox
                          isChecked={
                            isInStructure ||
                            selectedUsers.find(
                              (userAccountId) => userAccountId === value
                            )
                          }
                          onChange={({ target: { checked } }) =>
                            handleChangeInCheckbox(checked, value)
                          }
                          isDisabled={isInStructure}
                        />
                      </OptionWrapper>
                    </SelectableUserWrapper>
                  )
                )}
              </List>
              <WideButton
                style={{ marginTop: "24px", marginBottom: "20px" }}
                onClick={() => {
                  if (selectedUsers?.length) onSelect(selectedUsers);
                }}
              >
                Add selected
              </WideButton>
            </>
          ) : (
            <OptionLabel>No users found in collection.</OptionLabel>
          )}
        </>
      )}
    </FieldContainer>
  );
}

export default UsersFromCollection;
